import { animate, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, DatePipe, TitleCasePipe } from '@angular/common';
import { AfterContentInit, Component, ElementRef, Input, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterLink } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  BurgerMenuContent,
  Category,
  CategoryBurgerLink,
  Forest,
  Link,
  LinkContent,
  Shipping,
  StoreType,
  SubNavigationContent,
  TreeNode,
  isForest,
} from 'src/app/models';
import { buttonIcons } from 'src/app/modules/icon.module';
import { CategoryService } from 'src/app/services/category/category.service';
import { ContentService, sections } from 'src/app/services/content/content.service';
import { ShippingDialogService } from 'src/app/services/dialog/shipping-dialog.service';
import { KioskService } from 'src/app/services/kiosk/kiosk.service';
import { MobileService } from 'src/app/services/mobile.service';
import { ShippingService } from 'src/app/services/shipping';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { IconButtonComponent } from 'src/app/shared/components';
import { MemberBarComponent } from 'src/app/shared/components/auth';
import { environment } from 'src/environments/environment';
import { ContentSection } from '../../content-section';

@Component({
  selector: 'app-mobile-menu',
  templateUrl: './mobile-menu.component.html',
  styleUrls: ['./mobile-menu.component.scss'],
  animations: [
    trigger('installAnimation', [
      transition(':enter', [style({ width: 0 }), animate('800ms ease-out', style({ width: '*' }))]),
      transition(':leave', [style({ width: '*' }), animate('800ms ease-in', style({ width: 0 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MemberBarComponent, IconButtonComponent, MatIconModule, RouterLink, TitleCasePipe, AsyncPipe, DatePipe],
})
export class MobileMenuComponent extends ContentSection implements OnInit, AfterContentInit, OnDestroy {
  private mobileService = inject(MobileService);
  private shippingDialogService = inject(ShippingDialogService);
  private categoryService = inject(CategoryService);
  private shippingService = inject(ShippingService);
  private themeService = inject(ThemeService);
  private kioskService = inject(KioskService);
  private elRef = inject(ElementRef);
  private router = inject(Router);

  private subscription = new Subscription();

  contentUrl = `${environment.urls.content.merchant_content}/`;

  closeIcon = buttonIcons.bigCross;
  shippingIcon = buttonIcons.shipping;
  productUrl = `/${environment.urls.products}`;
  customerServiceUrl = `/${environment.urls.content.customer_service}`;

  @Input() open = false;

  storeClosed: boolean;
  enableMembers: boolean;

  shippingLabel: string;
  mainMenuTitle: string;
  groceriesTitle: string;
  categoriesTitle: string;
  groceries: Link;

  backButton: string;
  title: string;

  categories: BehaviorSubject<Forest<Category> | TreeNode<Category>>;
  parentCategory?: TreeNode<Category>;
  currentCategories: TreeNode<Category>[];
  childCategories?: TreeNode<Category>[];

  links: (SubNavigationContent | LinkContent | BurgerMenuContent | CategoryBurgerLink)[];

  isDagrofa: boolean;
  store: string;
  findShopUrl: string;

  hideCategories: boolean;
  isKiosk: boolean;

  shipping$: Observable<Shipping>;
  shippingValid$: BehaviorSubject<boolean>;

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, sections.header);

    this.storeClosed = this.contentService.getStoreClosed();

    this.shippingLabel = this.content.get('shippingLabelText');
    this.mainMenuTitle = this.content.get('mainMenuTitleText');
    this.groceriesTitle = this.content.get('groceriesTitleText');
    this.categoriesTitle = this.content.get('categoriesTitleText');

    this.groceries = { url: environment.urls.products, name: this.content.get('groceries') };

    const merchantContent = this.contentService.getMerchantContent();
    this.links = [];
    for (const entry of merchantContent) {
      switch (entry.type) {
        case 'simple': {
          const link = this.contentService.getContentLink(entry);
          this.links.push(link);
          break;
        }
        case 'link':
        case 'subNav':
          this.links.push(entry);
          break;
        case 'burger':
          if (!entry.hideOnMobile) this.links.push(entry);
          break;
        case 'categoryBurger': {
          if (entry.hideOnMobile) break;
          const link = this.categoryService.buildCategoryBurgerMenu(entry);
          if (link) this.links.push(link);
          break;
        }
      }
    }

    this.isDagrofa = this.contentService.isDagrofa;

    const type = this.contentService.getMerchant().type;
    if (type === StoreType.MENY) this.findShopUrl = 'https://meny.dk/find-butik';
    this.store = this.contentService.getStoreAddress();

    this.hideCategories = this.content.get('mobileHideCategories') || this.themeService.isOnline;
    this.isKiosk = this.kioskService.isKiosk;
  }

  ngOnInit() {
    this.enableMembers = this.contentService.getMerchant().enableMembers;

    this.title = this.mainMenuTitle;

    this.categories = new BehaviorSubject<Forest<Category> | TreeNode<Category>>(this.categoryService.getAllDisplayCategories());
    this.subscription.add(
      this.categories.subscribe((categories) => {
        this.mapCategories(categories);
      }),
    );

    this.shipping$ = this.shippingService.getShipping();
    this.shippingValid$ = this.shippingService.getShippingValid();
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  openShippingMenu() {
    this.shippingDialogService.openShippingDialog();
  }

  mapCategories(categories: Forest<Category> | TreeNode<Category>) {
    this.parentCategory = undefined;
    this.currentCategories = undefined;
    this.childCategories = undefined;

    if (categories) {
      if (isForest(categories)) {
        this.currentCategories = categories.trees.map((tree) => tree.root);
        return;
      }

      this.parentCategory = categories.parent;
      this.currentCategories = [categories];
      this.childCategories = categories.children;
    }
  }

  selectCategory(category: TreeNode<Category>) {
    if (category === undefined) {
      this.resetCategory();
      return;
    }

    if (!category.children?.length) {
      const url = this.getCategoryUrl(category.data.id);
      this.router.navigate([url]);
      this.close();
      return;
    }

    this.categories.next(category);
  }

  resetCategory() {
    this.categories.next(this.categoryService.getAllDisplayCategories());
  }

  getCategoryUrl(categoryId?: number): string {
    return this.categoryService.buildUrl(categoryId);
  }

  close() {
    this.mobileService.setOverlay(false);
  }

  sameSite(link: LinkContent) {
    return this.contentService.sameSite(link);
  }
}
